import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import usePostUserChangeRole from '../../../hooks/useApi/usePostUserChangeRole';
import ChangeUserRoleForm from './ChangeUserRoleForm';
import ChangeUserRoleContext, { ChangeUserRoleCtx } from './ChangeUserRoleContext';
import ChangeUserRoleDialogue from './ChangeUserRoleDialogue';
import { ChangeRoleDialogueHandler, DialogueHandlerProps } from '../types';
import { useTranslation } from 'react-i18next';

const { Box, Title, BackButton } = vp;

type FC = FunctionComponent;

const ChangeUserRoleContainer: FC = () => {
  const history = useHistory();
  const [ChangeUserRoleError, setChangeUserRoleError] = useState('');
  const [showChangeUserRoleErrorAlert, setShowChangeUserRoleErrorAlert] = useState(false);
  const [showChangeUserRoleSuccessAlert, setShowChangeUserRoleSuccessAlert] = useState(false);
  const [shouldChangeUserRole, setShouldChangeUserRole] = useState(false);
  const [changeRoleResponse, changeRole] = usePostUserChangeRole({});
  const { emailAddress, role } = useParams<{ emailAddress: string; role: string }>();
  const [reviewing, setReviewing] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Users.ChangeUserRole' });

  const [defaultUserRole, setDefaultUserRole] = useState<vp.UserChangeRoleRequest>({
    PartnerUserName: emailAddress,
    Role: role,
  });

  const ChangeUserRole: ChangeUserRoleCtx = {
    showChangeUserRoleSuccessAlert,
    showChangeUserRoleErrorAlert,
    ChangeUserRoleError,
  };

  const DefaultChangeUserRole: vp.UserChangeRoleRequest = {
    PartnerUserName: emailAddress,
    Role: role,
  };

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<ChangeRoleDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: ChangeRoleDialogueHandler = {
        change: {
          success: {
            default: () => {
              setShowChangeUserRoleSuccessAlert(false);
              history.push('/users');
            },
          },
          fail: {
            default: () => {
              setShowChangeUserRoleErrorAlert(false);
              setChangeUserRoleError('');
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
    onChangeUserRole: (values: vp.UserChangeRoleRequest) => {
      setShouldChangeUserRole(true);
      const UserChangeRoleRequest = { ...values };
      UserChangeRoleRequest.PartnerUserName = emailAddress;
      changeRole(UserChangeRoleRequest);
    },

    onReview: (values: vp.UserChangeRoleRequest) => {
      setDefaultUserRole(values);
      setReviewing(true);
    },
  };

  useEffect(() => {
    if (shouldChangeUserRole) {
      setShouldChangeUserRole(false);
      const { Success, ErrorMessage } = changeRoleResponse;
      if (Success === true) {
        setShowChangeUserRoleSuccessAlert(true);
      } else {
        setChangeUserRoleError(ErrorMessage);
        setShowChangeUserRoleErrorAlert(true);
      }
    }
  }, [changeRoleResponse]);

  return (
    <ChangeUserRoleContext.Provider value={ChangeUserRole}>
      <Box mt="sm" mb="sm" jc="fs" ai="center" gap="common">
        {reviewing ? (
          <>
            <BackButton
              variant="arrow"
              backButtonEnabled
              onClick={() => {
                setReviewing(false);
              }}
            />
            <Title title={t('Review.pageTitle')} />
          </>
        ) : (
          <>
            <BackButton variant="arrow" backButtonEnabled />
            <Title title={t('pageTitle', { userName: defaultUserRole.PartnerUserName })} />
          </>
        )}
      </Box>
      <ChangeUserRoleForm defaultValues={DefaultChangeUserRole} handlers={handlers} reviewing={reviewing} />
      <ChangeUserRoleDialogue handleDialogue={handlers.handleDialogue} />
    </ChangeUserRoleContext.Provider>
  );
};

export default ChangeUserRoleContainer;
